import {
  Search,
  Users,
  BookOpen,
  CalendarDays,
  MapPin,
  Sparkles,
  CheckCircle2,
  Circle,
} from "lucide-react";

export const MODULE_COLORS = {
  intranet: { text: "text-emerald-600", check: "text-emerald-500", tile: "bg-emerald-100 text-emerald-600", bg: "from-emerald-50/80" },
  operations: { text: "text-blue-600", check: "text-blue-500", tile: "bg-blue-100 text-blue-600", bg: "from-blue-50/80" },
  kpi: { text: "text-blue-600", check: "text-blue-500", tile: "bg-blue-100 text-blue-600", bg: "from-blue-50/80" },
  support: { text: "text-violet-600", check: "text-violet-500", tile: "bg-violet-100 text-violet-600", bg: "from-violet-50/80" },
  metier: { text: "text-orange-600", check: "text-orange-500", tile: "bg-orange-100 text-orange-600", bg: "from-orange-50/80" },
  magasin: { text: "text-emerald-600", check: "text-emerald-500", tile: "bg-emerald-100 text-emerald-600", bg: "from-emerald-50/80" },
};

const Sidebar = ({ items, active }) => (
  <div className="hidden w-24 shrink-0 flex-col gap-1 bg-[#0A0F1D] p-2.5 sm:flex">
    <img src="/assets/orbit-logo-white.png" alt="Orbit" className="mb-2 h-4 w-auto object-contain object-left" />
    {items.map((item) => (
      <span
        key={item}
        className={`rounded-md px-2 py-1.5 text-[8px] font-semibold ${
          item === active ? "bg-blue-600 text-white" : "text-slate-400"
        }`}
      >
        {item}
      </span>
    ))}
  </div>
);

export const IntranetMock = () => (
  <div className="flex overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_32px_64px_-24px_rgba(10,15,29,0.25)]">
    <Sidebar items={["Accueil", "Actualités", "OrbitGram", "Ressources", "Annuaire", "Événements"]} active="Actualités" />
    <div className="min-w-0 flex-1 p-4">
      <div className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-1.5 text-[9px] text-slate-400">
        <Search className="h-3 w-3" /> Rechercher une actualité, un document, un collaborateur…
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">À la une</p>
          <div className="mt-1.5 overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 p-4">
            <p className="font-display text-xs font-bold text-white">Nouvelle campagne printemps</p>
            <p className="mt-1 text-[9px] text-emerald-100">Découvrez le dispositif à déployer en magasin.</p>
            <span className="mt-2 inline-block rounded-md bg-white px-2 py-1 text-[8px] font-bold text-slate-900">
              Voir le kit
            </span>
          </div>
          <div className="mt-2.5 space-y-1.5">
            {["Résultats T1 : bravo à toutes les équipes !", "Bienvenue à nos 3 nouveaux managers"].map((n) => (
              <div key={n} className="flex items-center gap-2 rounded-lg border border-slate-50 p-2">
                <span className="h-6 w-8 rounded bg-slate-100" />
                <p className="text-[9px] font-semibold text-slate-700">{n}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Accès rapides</p>
          <div className="mt-1.5 space-y-1.5">
            {[
              { label: "Documents", Icon: BookOpen },
              { label: "Formations", Icon: BookOpen },
              { label: "Vie d'entreprise", Icon: CalendarDays },
              { label: "Annuaire", Icon: Users },
            ].map(({ label, Icon }) => (
              <div key={label} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-2">
                <Icon className="h-3 w-3 text-blue-600" />
                <span className="text-[9px] font-semibold text-slate-700">{label}</span>
              </div>
            ))}
          </div>
          <div className="mt-2.5 rounded-xl border border-slate-100 p-2.5">
            <p className="text-[8px] font-bold uppercase tracking-wider text-emerald-500">OrbitGram</p>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-500 text-[8px] font-bold text-white">
                DM
              </span>
              <div>
                <p className="text-[9px] font-bold text-slate-800">Daphné Martin</p>
                <p className="text-[8px] text-slate-400">Lille Centre</p>
              </div>
            </div>
            <p className="mt-1.5 text-[8px] leading-snug text-slate-500">
              Belle mise en place de la nouvelle vitrine ! Bravo à toute l'équipe
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const OpsMock = () => (
  <div className="flex overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_32px_64px_-24px_rgba(10,15,29,0.25)]">
    <Sidebar items={["Vue d'ensemble", "Mes magasins", "Alertes", "Plans d'action", "Visites & audits", "Objectifs"]} active="Vue d'ensemble" />
    <div className="min-w-0 flex-1 p-4">
      <div className="flex flex-wrap gap-1.5 text-[8px] font-semibold text-slate-500">
        {["Mon secteur", "Toutes les régions", "Cette semaine"].map((f) => (
          <span key={f} className="rounded-md border border-slate-100 px-2 py-1">{f} ▾</span>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2">
        {[
          { value: "24", label: "magasins", color: "text-slate-900" },
          { value: "16", label: "OK", color: "text-emerald-500" },
          { value: "6", label: "En alerte", color: "text-amber-500" },
          { value: "2", label: "Critiques", color: "text-rose-500" },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-lg bg-slate-50 p-2 text-center">
            <p className={`font-mono-orbit text-sm font-bold ${kpi.color}`}>{kpi.value}</p>
            <p className="text-[7px] font-semibold uppercase tracking-wide text-slate-400">{kpi.label}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div className="relative h-36 overflow-hidden rounded-xl bg-slate-50">
          <div className="absolute inset-0 bg-grid-light" />
          {[
            { top: "18%", left: "30%", c: "text-emerald-500" },
            { top: "35%", left: "55%", c: "text-emerald-500" },
            { top: "50%", left: "20%", c: "text-amber-400" },
            { top: "60%", left: "70%", c: "text-emerald-500" },
            { top: "30%", left: "75%", c: "text-rose-500" },
            { top: "70%", left: "45%", c: "text-blue-500" },
          ].map((pin, i) => (
            <MapPin key={i} className={`absolute h-4 w-4 ${pin.c}`} style={{ top: pin.top, left: pin.left }} fill="currentColor" />
          ))}
          <span className="absolute bottom-2 left-2 rounded bg-white/90 px-1.5 py-0.5 text-[7px] font-bold text-slate-500">
            Lille & Nord — 12 magasins
          </span>
        </div>
        <div className="rounded-xl border border-slate-100 p-3">
          <p className="text-[9px] font-bold text-slate-700">Classement magasins</p>
          {[
            { name: "Lille Centre", score: 96 },
            { name: "Bordeaux", score: 93 },
            { name: "Lyon Part-Dieu", score: 89 },
            { name: "Marseille Prado", score: 80 },
            { name: "Toulouse", score: 72 },
          ].map((store, i) => (
            <div key={store.name} className="mt-1.5 flex items-center gap-2">
              <span className="font-mono-orbit w-3 text-[8px] text-slate-400">{i + 1}</span>
              <span className="w-24 truncate text-[9px] font-medium text-slate-600">{store.name}</span>
              <div className="h-1.5 flex-1 rounded-full bg-slate-100">
                <div
                  className={`h-1.5 rounded-full ${store.score >= 85 ? "bg-emerald-500" : store.score >= 78 ? "bg-amber-400" : "bg-rose-400"}`}
                  style={{ width: `${store.score}%` }}
                />
              </div>
              <span className="font-mono-orbit text-[8px] font-bold text-slate-700">{store.score}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const KpiMock = () => (
  <div className="flex overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_32px_64px_-24px_rgba(10,15,29,0.25)]">
    <Sidebar items={["Performance", "Ventes", "Achats", "Inventaires", "RH", "Qualité"]} active="Performance" />
    <div className="min-w-0 flex-1 p-4">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {[
          { label: "CA", value: "1 246 500 €", trend: "+6,2%", up: true },
          { label: "Marge brute", value: "72%", trend: "+1,4 pts", up: true },
          { label: "Panier moyen", value: "18,60 €", trend: "-0,7%", up: false },
          { label: "Clients", value: "68 420", trend: "+3,2%", up: true },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-lg border border-slate-100 p-2.5">
            <p className="text-[7px] font-semibold uppercase tracking-wide text-slate-400">{kpi.label}</p>
            <p className="font-mono-orbit text-xs font-bold text-slate-900">{kpi.value}</p>
            <p className={`text-[8px] font-bold ${kpi.up ? "text-emerald-500" : "text-rose-500"}`}>{kpi.trend}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 grid gap-3 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-xl border border-slate-100 p-3">
          <p className="text-[9px] font-bold text-slate-700">Évolution du CA</p>
          <div className="mt-2 flex h-28 items-end gap-1.5">
            {[42, 55, 48, 62, 58, 70, 65, 78, 74, 88, 82, 95].map((h, i) => (
              <div key={i} className={`flex-1 rounded-t-sm ${i >= 9 ? "bg-blue-600" : "bg-blue-400/60"}`} style={{ height: `${h}%` }} />
            ))}
          </div>
          <div className="mt-1.5 flex justify-between text-[7px] text-slate-400">
            <span>Jan</span><span>Mar</span><span>Mai</span><span>Juil</span><span>Sept</span><span>Nov</span>
          </div>
        </div>
        <div className="rounded-xl bg-violet-600 p-3.5 text-white">
          <p className="flex items-center gap-1.5 text-[9px] font-extrabold uppercase tracking-wider">
            <Sparkles className="h-3 w-3" /> Orbit IA
          </p>
          <p className="mt-2 text-[10px] font-bold leading-snug">Pourquoi mon secteur décroche cette semaine ?</p>
          <p className="mt-1.5 text-[8px] leading-relaxed text-violet-100">
            Le CA est en baisse de 8% sur votre secteur, principalement en raison d'une baisse de fréquentation sur
            3 magasins (Lyon, Marseille, Toulouse).
          </p>
          <p className="mt-2 text-[8px] font-bold uppercase tracking-wide text-violet-200">Recommandations</p>
          <ul className="mt-1 space-y-0.5 text-[8px] text-violet-100">
            <li>· Renforcer les opérations commerciales</li>
            <li>· Vérifier la disponibilité des produits phares</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

export const SupportMock = () => (
  <div className="flex overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_32px_64px_-24px_rgba(10,15,29,0.25)]">
    <Sidebar items={["Toutes (24)", "À traiter (10)", "En cours (9)", "Résolues (12)", "Maintenance", "RH"]} active="En cours (9)" />
    <div className="min-w-0 flex-1 p-4">
      <div className="flex items-center justify-between">
        <p className="font-display text-xs font-bold text-slate-900">Panne chambre froide</p>
        <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[8px] font-bold text-blue-600">En traitement</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[8px] font-semibold">
        <span className="rounded-md bg-slate-50 px-2 py-1 text-slate-500">Lille Centre</span>
        <span className="rounded-md bg-slate-50 px-2 py-1 text-slate-500">Maintenance</span>
        <span className="rounded-md bg-rose-50 px-2 py-1 text-rose-600">Priorité haute</span>
      </div>
      <p className="mt-2.5 text-[9px] leading-relaxed text-slate-500">
        La chambre froide ne maintient plus la température depuis ce matin. Voir photos ci-jointes.
      </p>
      <div className="mt-3 rounded-xl border border-slate-100 p-3">
        <p className="text-[9px] font-bold text-slate-700">Suivi de la demande</p>
        <div className="mt-2 space-y-2">
          {[
            { time: "09:14", label: "Demande envoyée", state: "done" },
            { time: "09:16", label: "Reçue par le support", state: "done" },
            { time: "09:24", label: "Affectée à l'équipe maintenance", state: "done" },
            { time: "11:42", label: "Intervention planifiée", state: "done" },
            { time: "14:05", label: "Magasin informé", state: "current" },
            { time: "—", label: "Résolution", state: "todo" },
          ].map((step) => (
            <div key={step.label} className="flex items-center gap-2.5">
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${
                  step.state === "done" ? "bg-emerald-500" : step.state === "current" ? "bg-blue-500 ring-4 ring-blue-100" : "bg-slate-200"
                }`}
              />
              <span className="font-mono-orbit w-8 text-[8px] text-slate-400">{step.time}</span>
              <span className={`text-[9px] font-medium ${step.state === "todo" ? "text-slate-400" : "text-slate-700"}`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const MetierMock = () => (
  <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_32px_64px_-24px_rgba(10,15,29,0.25)]">
    <div className="flex gap-1 overflow-x-auto border-b border-slate-100 p-2">
      {["Hygiène & HACCP", "Inventaires", "Produits", "Merchandising", "Contrôles", "Process personnalisés"].map((tab, i) => (
        <span
          key={tab}
          className={`whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[8px] font-bold ${
            i === 0 ? "bg-orange-500 text-white" : "text-slate-500"
          }`}
        >
          {tab}
        </span>
      ))}
    </div>
    <div className="p-4">
      <div className="flex items-center justify-between">
        <p className="font-display text-xs font-bold text-slate-900">Contrôle températures</p>
        <span className="text-[8px] text-slate-400">Bordeaux Lac · 12/03 · 08:00</span>
      </div>
      <div className="mt-2.5 space-y-1.5">
        {[
          { label: "Chambre froide", value: "+3,2°C", status: "Conforme", ok: true },
          { label: "Congélateur", value: "-18,1°C", status: "Conforme", ok: true },
          { label: "Vitrine produits", value: "+4,8°C", status: "À surveiller", ok: false },
          { label: "Chambre négative", value: "-17,5°C", status: "Conforme", ok: true },
          { label: "Préparation", value: "+5,1°C", status: "Conforme", ok: true },
        ].map((row) => (
          <div key={row.label} className="flex items-center gap-2 rounded-lg border border-slate-50 px-2.5 py-1.5">
            <span className="flex-1 text-[9px] font-semibold text-slate-700">{row.label}</span>
            <span className="font-mono-orbit text-[9px] font-bold text-slate-900">{row.value}</span>
            <span
              className={`rounded-full px-2 py-0.5 text-[7px] font-bold ${
                row.ok ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
              }`}
            >
              {row.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const StoreMock = () => (
  <div className="mx-auto w-64 rounded-[1.8rem] border-[6px] border-slate-900 bg-white p-3.5 shadow-[0_32px_64px_-24px_rgba(10,15,29,0.35)]">
    <div className="flex items-center justify-between">
      <p className="font-display text-[11px] font-bold text-slate-900">Ma journée</p>
      <span className="text-[7px] text-slate-400">Mar. 8 sept.</span>
    </div>
    <div className="relative mx-auto my-2.5 h-16 w-16">
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r="28" fill="none" stroke="#E2E8F0" strokeWidth="7" />
        <circle
          cx="32" cy="32" r="28" fill="none" stroke="#10B981" strokeWidth="7" strokeLinecap="round"
          strokeDasharray={`${((2 * Math.PI * 28) / 6) * 4} ${2 * Math.PI * 28}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono-orbit text-xs font-bold text-slate-900">4/6</span>
        <span className="text-[6px] text-slate-400">tâches</span>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-1.5">
      {[
        { label: "CA", value: "4 320 €" },
        { label: "Objectif", value: "4 500 €" },
        { label: "Panier moy.", value: "16,60 €" },
      ].map((kpi) => (
        <div key={kpi.label} className="rounded-lg bg-slate-50 p-1.5 text-center">
          <p className="text-[6px] font-semibold uppercase text-slate-400">{kpi.label}</p>
          <p className="font-mono-orbit text-[9px] font-bold text-slate-900">{kpi.value}</p>
        </div>
      ))}
    </div>
    <p className="mt-2.5 text-[8px] font-bold uppercase tracking-wider text-slate-400">Mes rituels</p>
    <div className="mt-1.5 flex justify-between">
      {[
        { label: "Ouverture", done: true },
        { label: "Caisse", done: true },
        { label: "Vitrine", done: true },
        { label: "Hygiène", done: false },
        { label: "Fermeture", done: false },
      ].map((ritual) => (
        <div key={ritual.label} className="flex flex-col items-center gap-1">
          <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${ritual.done ? "bg-emerald-100" : "bg-slate-100"}`}>
            {ritual.done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <Circle className="h-3.5 w-3.5 text-slate-300" />}
          </span>
          <span className="text-[6px] font-semibold text-slate-500">{ritual.label}</span>
        </div>
      ))}
    </div>
    <p className="mt-2.5 text-[8px] font-bold uppercase tracking-wider text-slate-400">Mes demandes</p>
    <div className="mt-1.5 flex gap-1.5">
      <span className="flex-1 rounded-lg bg-blue-50 px-2 py-1 text-center text-[8px] font-bold text-blue-600">1 en cours</span>
      <span className="flex-1 rounded-lg bg-slate-50 px-2 py-1 text-center text-[8px] font-bold text-slate-400">0 attente</span>
      <span className="flex-1 rounded-lg bg-emerald-50 px-2 py-1 text-center text-[8px] font-bold text-emerald-600">2 résolues</span>
    </div>
  </div>
);

export const MOCKS = {
  intranet: IntranetMock,
  operations: OpsMock,
  kpi: KpiMock,
  support: SupportMock,
  metier: MetierMock,
  magasin: StoreMock,
};

export const MODULE_SCREENSHOTS = {
  intranet: "/assets/modules/intranet.png",
  operations: "/assets/modules/operations.png",
  kpi: "/assets/modules/kpi.png",
  support: "/assets/modules/support.png",
  metier: "/assets/modules/metier.png",
  magasin: "/assets/modules/magasin.png",
};

export const MODULE_SECONDARY = {
  operations: ["/assets/modules/operations-activite.png", "/assets/modules/operations-plan.png"],
  kpi: ["/assets/modules/kpi-rh.png"],
};

export const ModuleVisual = ({ id }) => {
  const src = MODULE_SCREENSHOTS[id];
  if (src) {
    return (
      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_32px_64px_-24px_rgba(10,15,29,0.3)]">
        <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-2">
          <span className="h-2 w-2 rounded-full bg-rose-400" />
          <span className="h-2 w-2 rounded-full bg-amber-400" />
          <span className="h-2 w-2 rounded-full bg-emerald-400" />
          <span className="ml-2 flex-1 rounded-md border border-slate-100 bg-white px-2 py-0.5 font-mono-orbit text-[9px] text-slate-400">
            app.orbit.fr
          </span>
        </div>
        <img src={src} alt={`Interface Orbit — module ${id}`} className="block w-full" loading="lazy" />
      </div>
    );
  }
  const Mock = MOCKS[id];
  return Mock ? <Mock /> : null;
};
